// The employer satisfaction meter: the game's half dial (dial.png, a copy of
// assets/sprites/ui/trouble-dial-face.png) with a needle for the room's mood.
// Red at the left is -1, straight up is 0, green at the right is 1. The plate's
// geometry is the game's: see src/objects/ui/dial_face.gd.

import { prefersLessMotion } from './dom';

const PLATE_WIDTH = 25;
const PLATE_HEIGHT = 13;
const PIVOT_X = 12;
const PIVOT_Y = 12;
const NEEDLE_LENGTH = 10;
// The needle stops short of the flat base at either end, as in the game.
const SWEEP_INSET = 0.3;
const NEEDLE_COLOR = '#0a061e';
const HUB_COLOR = '#ece8ff';

// A spring with a little overshoot, so a change in the room's mood swings in.
const STIFFNESS = 60;
const DAMPING = 7;
const SETTLED = 0.001;
const STEP_MOST_SEC = 0.05;

export interface Meter {
  canvas: HTMLCanvasElement;
  setMood: (mood: number | null) => void;
}

const angleOf = (mood: number): number => Math.PI / 2 - mood * (Math.PI / 2 - SWEEP_INSET);

// A line in whole pixels from the pivot outward.
function drawNeedle(context: CanvasRenderingContext2D, angle: number): void {
  context.fillStyle = NEEDLE_COLOR;
  for (let step = 0; step <= NEEDLE_LENGTH; step++) {
    const x = Math.round(PIVOT_X + Math.cos(angle) * step);
    const y = Math.round(PIVOT_Y - Math.sin(angle) * step);
    context.fillRect(x, y, 1, 1);
  }
  context.fillRect(PIVOT_X - 1, PIVOT_Y - 1, 3, 2);
  context.fillStyle = HUB_COLOR;
  context.fillRect(PIVOT_X, PIVOT_Y - 1, 1, 1);
}

export function makeMeter(cssScale: number): Meter {
  const canvas = document.createElement('canvas');
  canvas.className = 'meter';
  const ratio = window.devicePixelRatio || 1;
  const device = Math.max(1, Math.round(cssScale * ratio));
  canvas.width = PLATE_WIDTH * device;
  canvas.height = PLATE_HEIGHT * device;
  canvas.style.width = `${canvas.width / ratio}px`;
  canvas.style.height = `${canvas.height / ratio}px`;
  const art = document.createElement('canvas');
  art.width = PLATE_WIDTH;
  art.height = PLATE_HEIGHT;
  const artContext = art.getContext('2d') as CanvasRenderingContext2D;
  const out = canvas.getContext('2d') as CanvasRenderingContext2D;
  const plate = new Image();
  plate.src = '/dial.png';

  let target = 0;
  let position = 0;
  let speed = 0;
  let last = 0;
  let running = false;

  const draw = (): void => {
    artContext.clearRect(0, 0, PLATE_WIDTH, PLATE_HEIGHT);
    if (plate.complete) artContext.drawImage(plate, 0, 0);
    drawNeedle(artContext, angleOf(position));
    out.imageSmoothingEnabled = false;
    out.clearRect(0, 0, canvas.width, canvas.height);
    out.drawImage(art, 0, 0, canvas.width, canvas.height);
  };

  const frame = (now: number): void => {
    const dt = Math.min(STEP_MOST_SEC, (now - last) / 1000);
    last = now;
    speed += (STIFFNESS * (target - position) - DAMPING * speed) * dt;
    position += speed * dt;
    draw();
    if (Math.abs(target - position) < SETTLED && Math.abs(speed) < SETTLED) {
      running = false;
      return;
    }
    window.requestAnimationFrame(frame);
  };

  plate.addEventListener('load', draw);
  draw();

  return {
    canvas,
    setMood: (mood) => {
      target = mood ?? 0;
      canvas.classList.toggle('is-empty', mood === null);
      if (prefersLessMotion()) {
        position = target;
        draw();
        return;
      }
      if (running) return;
      running = true;
      last = performance.now();
      window.requestAnimationFrame(frame);
    },
  };
}
